/**
 * CSW Homepage redesigned with Paris-inspired aesthetic.
 * Deep navy, crisp whites, bold color blocking, premium Korean brand feel.
 * Dynamic asymmetric layouts with editorial rhythm.
 */

import { Helmet } from "react-helmet-async";
import {
  Coffee, BookOpen, Briefcase, Timer, BookCheck, UsersRound,
  CheckSquare, Bell, Video, Users, ArrowRight, VolumeX,
  Clock, Eye, Radio, Globe, Heart, Sparkles, Wifi
} from "lucide-react";
import { Link } from "react-router-dom";

/* ─── Modes ─── */
const modes = [
  {
    id: "chill",
    title: "Chill Mode",
    tagline: "Recharge & Connect",
    description:
      "Sometimes the best way to recharge your brain is to hang out with others in a low-pressure environment. Chill Mode lets you stream music, watch videos or chat while enjoying the virtual company of friends. Research on online communities notes that feelings of connection, belonging and shared values are essential for creating supportive learning environments.",
    icon: Coffee,
    features: [
      { icon: Video, label: "Stream Together" },
      { icon: Users, label: "Virtual Hangouts" },
    ],
  },
  {
    id: "study",
    title: "Study Mode",
    tagline: "Focus & Achieve",
    description:
      "When it's time to hit the books, Study Mode provides structured tools and accountability features. Join focus rooms with Pomodoro timers, share your goals, and see your progress alongside others. Students who engage in study sessions are 30% more likely to succeed.",
    icon: BookOpen,
    features: [
      { icon: Timer, label: "Pomodoro Timer" },
      { icon: BookCheck, label: "Quizzes" },
      { icon: UsersRound, label: "Focus Groups" },
    ],
  },
  {
    id: "work",
    title: "Work Mode",
    tagline: "Plan & Execute",
    description:
      "For heavier projects or professional tasks, Work Mode combines task management with reminders and analytics. Create Kanban boards, set deadlines, and track your time. Gamified features like streaks and progress badges yield a large positive effect on outcomes.",
    icon: Briefcase,
    features: [
      { icon: CheckSquare, label: "Kanban Tasks" },
      { icon: Bell, label: "Smart Reminders" },
    ],
  },
];

/* ─── Room Types ─── */
const roomTypes = [
  { id: "pomodoro", title: "Pomodoro Rooms", description: "Synchronized blocks of 25–50 minutes with short breaks. Timed sessions align with natural attention cycles.", icon: Timer, link: "/study-timer/" },
  { id: "silent", title: "Silent Study", description: "Cameras on, microphones muted. Mirrors the quiet of a library while providing visual accountability.", icon: VolumeX, link: "/study-room/" },
  { id: "collaborative", title: "Collaborative Rooms", description: "Talk through problems, brainstorm and share screens. Explaining concepts to others improves retention.", icon: Users, link: "/group-study/" },
];

/* ─── Study Stream Features ─── */
const streamFeatures = [
  { icon: Video, title: "Cameras On", description: "See and be seen for visual accountability" },
  { icon: Clock, title: "Shared Timers", description: "Synchronized Pomodoro sessions" },
  { icon: Users, title: "Ritual Check-ins", description: "Light interaction without pressure" },
  { icon: Eye, title: "Virtual Library", description: "Constant presence provides solidarity" },
];

/* ─── Study Modes ─── */
const studyModes = [
  { id: "study-with-me", title: "Study With Me", description: "Study alongside a host who broadcasts their session. Viewers use these to personalise their ambience, create peer pressure and gain emotional support.", icon: Video, link: "/study-with-me/" },
  { id: "study-with-friends", title: "Study with Friends", description: "Invite classmates or colleagues into a private room. Small groups are ideal for accountability and collaboration.", icon: Users, link: "/study-with-friends/" },
  { id: "study-with-strangers", title: "Study With Strangers", description: "Join a public room and meet students and professionals from around the world. New perspectives and motivation await.", icon: Globe, link: "/study-with-strangers/" },
  { id: "group-study", title: "Group Study", description: "Larger study groups offer diversity and expertise. Shared notes, polls and group leaderboards turn study into a team sport.", icon: UsersRound, link: "/group-study/" },
  { id: "zoom-study-room", title: "Zoom Study Room", description: "High-definition video, screen share and chat. Face-to-face accountability with the convenience of home.", icon: Video, link: "/zoom-study-room/" },
  { id: "study-together", title: "Study Together Online", description: "CSW isn't just a tool – it's a movement. 24/7 rooms, cross-time-zone scheduling and a global community.", icon: Heart, link: "/online-study/" },
];

const RedesignPreview = () => {
  return (
    <div className="paris-redesign min-h-screen">
      <Helmet>
        <title>CSW Redesign Preview — Paris Style</title>
        <meta name="robots" content="noindex, nofollow" />
        <link
          href="https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;700;900&family=Playfair+Display:wght@400;600;700;800&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      <style>{`
        .paris-redesign {
          --p-navy: 222 47% 18%;
          --p-navy-deep: 222 50% 12%;
          --p-blue: 215 70% 45%;
          --p-sky: 210 60% 55%;
          --p-white: 0 0% 100%;
          --p-cream: 40 30% 97%;
          --p-warm: 35 25% 93%;
          --p-text: 220 15% 30%;
          --p-muted: 220 10% 55%;
          --p-border: 220 15% 88%;
          --p-gold: 40 70% 50%;

          background: hsl(var(--p-cream));
          color: hsl(var(--p-text));
          font-family: 'Noto Sans KR', system-ui, sans-serif;
          font-weight: 400;
        }
        .paris-redesign h1, .paris-redesign h2, .paris-redesign h3, .paris-redesign h4 {
          font-family: 'Playfair Display', serif;
          font-weight: 700;
        }
      `}</style>

      {/* ─── NAVBAR ─── */}
      <header
        className="fixed top-0 left-0 right-0 z-50"
        style={{ backgroundColor: "hsl(222, 47%, 18%)" }}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-xl font-bold tracking-wide text-white" style={{ fontFamily: "'Playfair Display', serif" }}>CSW</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: "Study Stream", href: "/study-stream/" },
              { label: "Timer", href: "/study-timer/" },
              { label: "Workspace", href: "/online-workspace/" },
              { label: "Blog", href: "/blog/" },
            ].map((l) => (
              <a key={l.label} href={l.href} className="text-sm font-medium text-white/70 hover:text-white transition-colors">{l.label}</a>
            ))}
            <a
              href="https://app.csw.live"
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2 text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ backgroundColor: "hsl(215, 70%, 45%)" }}
            >
              Start Studying
            </a>
          </div>
        </nav>
      </header>

      {/* ─── HERO ─── */}
      <section className="relative pt-16 overflow-hidden" style={{ backgroundColor: "hsl(222, 47%, 18%)" }}>
        <div className="absolute inset-0 opacity-10" style={{ background: "linear-gradient(135deg, hsl(215, 70%, 45%) 0%, transparent 50%)" }} />

        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-32 relative z-10">
          {/* Top banner */}
          <div className="flex justify-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2" style={{ backgroundColor: "hsla(215, 70%, 45%, 0.15)", border: "1px solid hsla(215, 70%, 45%, 0.3)" }}>
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "hsl(215, 70%, 55%)" }} />
              <span className="text-sm font-medium text-white/80">Free to use · No limits</span>
            </div>
          </div>

          {/* Centered headline */}
          <div className="text-center max-w-4xl mx-auto mb-14">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Chill, Study, Work
            </h1>
            <p className="text-xl lg:text-2xl font-light leading-relaxed text-white/50 mb-8">
              Your Personal Productivity Tool & Online Workspace
            </p>
            <p className="text-base leading-relaxed max-w-2xl mx-auto text-white/40">
              CSW is a community-powered productivity hub for students, professionals, and lifelong learners who thrive when they're connected.
            </p>
          </div>

          {/* CTA row */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-16">
            <a
              href="https://app.csw.live"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-bold text-white transition-all hover:opacity-90 hover:scale-[1.02]"
              style={{ backgroundColor: "hsl(215, 70%, 45%)", boxShadow: "0 4px 20px hsla(215, 70%, 45%, 0.4)" }}
            >
              Be Productive in 10 Seconds <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://app.csw.live"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-medium border transition-all hover:bg-white/5"
              style={{ borderColor: "hsla(0, 0%, 100%, 0.2)", color: "white" }}
            >
              Try our beta
            </a>
          </div>

          {/* Video full-width */}
          <div className="relative max-w-5xl mx-auto">
            <div className="overflow-hidden border shadow-2xl" style={{ borderColor: "hsla(0, 0%, 100%, 0.08)", boxShadow: "0 40px 100px hsla(222, 50%, 5%, 0.6)" }}>
              <video autoPlay muted loop playsInline className="w-full h-auto">
                <source src="/videos/productivity-demo.mp4" type="video/mp4" />
              </video>
            </div>
            {/* Floating badges */}
            <div className="absolute -bottom-5 left-6 px-5 py-3 shadow-lg flex items-center gap-2" style={{ backgroundColor: "hsl(215, 70%, 45%)", color: "white" }}>
              <Wifi className="w-4 h-4" />
              <span className="text-sm font-bold">42 studying now</span>
            </div>
            <div className="absolute -bottom-5 right-6 flex gap-1">
              {[
                { val: "10,000+", label: "Users" },
                { val: "24/7", label: "Rooms" },
                { val: "Free", label: "Forever" },
              ].map((s) => (
                <div key={s.label} className="px-4 py-3 text-center" style={{ backgroundColor: "hsl(222, 47%, 18%)", border: "1px solid hsla(215, 70%, 45%, 0.3)" }}>
                  <p className="text-lg font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{s.val}</p>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
          {/* Bottom spacer for floating elements */}
          <div className="h-12" />
        </div>
      </section>

      {/* ─── MODES — Staggered cards ─── */}
      <section className="py-28 px-6 md:px-12" style={{ backgroundColor: "hsl(var(--p-cream))" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 mb-16">
            <div className="lg:col-span-5">
              <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "hsl(215, 70%, 45%)" }}>Three Powerful Modes</p>
              <h2 className="text-4xl lg:text-5xl leading-tight" style={{ color: "hsl(222, 47%, 18%)" }}>
                Built for How<br />You Actually Work
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-base leading-relaxed" style={{ color: "hsl(var(--p-muted))" }}>
                CSW adapts to your workflow — whether you're unwinding, grinding, or executing. Switch between modes seamlessly.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-0">
            {modes.map((mode, index) => {
              const Icon = mode.icon;
              return (
                <div
                  key={mode.id}
                  className="group relative transition-all duration-300"
                  style={{
                    backgroundColor: index === 1 ? "hsl(222, 47%, 18%)" : "white",
                    marginTop: index === 1 ? "-20px" : index === 2 ? "20px" : "0",
                    border: index === 1 ? "none" : "1px solid hsl(var(--p-border))",
                  }}
                >
                  {/* Top accent bar */}
                  <div className="h-1 w-full" style={{ backgroundColor: "hsl(215, 70%, 45%)" }} />

                  <div className="p-8 lg:p-10">
                    <div className="flex items-center gap-4 mb-6">
                      <div
                        className="w-14 h-14 flex items-center justify-center"
                        style={{ backgroundColor: index === 1 ? "hsl(215, 70%, 45%)" : "hsl(222, 47%, 18%)" }}
                      >
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <span
                          className="text-[10px] font-bold tracking-[0.15em] uppercase block mb-1"
                          style={{ color: index === 1 ? "hsl(210, 60%, 65%)" : "hsl(215, 70%, 45%)" }}
                        >
                          {mode.tagline}
                        </span>
                        <h3
                          className="text-2xl"
                          style={{ color: index === 1 ? "white" : "hsl(222, 47%, 18%)" }}
                        >
                          {mode.title}
                        </h3>
                      </div>
                    </div>

                    <p
                      className="text-sm leading-relaxed mb-8"
                      style={{ color: index === 1 ? "hsla(0, 0%, 100%, 0.55)" : "hsl(var(--p-muted))" }}
                    >
                      {mode.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {mode.features.map((f) => {
                        const FIcon = f.icon;
                        return (
                          <div
                            key={f.label}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium"
                            style={{
                              backgroundColor: index === 1 ? "hsla(215, 70%, 45%, 0.2)" : "hsl(var(--p-warm))",
                              color: index === 1 ? "white" : "hsl(222, 47%, 18%)",
                              border: index === 1 ? "1px solid hsla(215, 70%, 45%, 0.3)" : "1px solid hsl(var(--p-border))",
                            }}
                          >
                            <FIcon className="w-3.5 h-3.5" style={{ color: index === 1 ? "hsl(210, 60%, 65%)" : "hsl(215, 70%, 45%)" }} />
                            <span>{f.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── STUDY ROOMS — Horizontal strip ─── */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "hsl(222, 47%, 18%)" }}>
        <div className="grid lg:grid-cols-12">
          {/* Left info panel */}
          <div className="lg:col-span-5 p-10 lg:p-16 flex flex-col justify-center">
            <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-4" style={{ color: "hsl(210, 60%, 65%)" }}>Real-Time Focus Hubs</p>
            <h2 className="text-4xl lg:text-5xl text-white leading-tight mb-6">
              Study Rooms
            </h2>
            <p className="text-base leading-relaxed text-white/50 mb-8">
              CSW's study rooms mimic the library experience by pairing video presence with digital tools. Live video, screen-sharing and chat create what psychologists call <strong className="text-white/70">social facilitation</strong> — the tendency to perform better in the presence of others.
            </p>
            <a
              href="https://app.csw.live"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-bold transition-colors"
              style={{ color: "hsl(210, 60%, 65%)" }}
            >
              Explore Rooms <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Right cards — stacked */}
          <div className="lg:col-span-7 p-6 lg:p-10">
            <div className="space-y-4">
              {roomTypes.map((room) => {
                const Icon = room.icon;
                return (
                  <Link
                    key={room.id}
                    to={room.link}
                    className="group flex items-start gap-6 p-6 transition-all duration-300 hover:translate-x-2"
                    style={{ backgroundColor: "hsla(222, 47%, 24%, 1)", border: "1px solid hsla(215, 70%, 45%, 0.15)" }}
                  >
                    <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: "hsl(215, 70%, 45%)" }}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg text-white mb-1">{room.title}</h4>
                      <p className="text-sm text-white/45 leading-relaxed">{room.description}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-white/20 group-hover:text-white/60 transition-colors mt-1 flex-shrink-0" />
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─── STUDY STREAM — Offset layout ─── */}
      <section className="py-28 px-6 md:px-12" style={{ backgroundColor: "white" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-10">
            {/* Left — features grid */}
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4 mb-4">
                {streamFeatures.map((f) => (
                  <div
                    key={f.title}
                    className="group p-6 transition-all duration-300 hover:-translate-y-1"
                    style={{ backgroundColor: "hsl(var(--p-cream))", border: "1px solid hsl(var(--p-border))" }}
                  >
                    <div className="w-12 h-12 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{ backgroundColor: "hsl(222, 47%, 18%)" }}>
                      <f.icon className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="text-sm font-bold mb-1" style={{ color: "hsl(222, 47%, 18%)" }}>{f.title}</h4>
                    <p className="text-xs" style={{ color: "hsl(var(--p-muted))" }}>{f.description}</p>
                  </div>
                ))}
              </div>

              {/* Stats bar */}
              <div className="p-5 flex items-center justify-between" style={{ backgroundColor: "hsl(222, 47%, 18%)" }}>
                {[
                  { val: "∞", label: "Free Streams" },
                  { val: "24/7", label: "Availability" },
                  { val: "$0", label: "Always Free" },
                ].map((s, i) => (
                  <div key={s.label} className="flex items-center gap-4">
                    {i > 0 && <div className="w-px h-10 bg-white/15" />}
                    <div className="text-center flex-1">
                      <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{s.val}</p>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider">{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — text + CTA */}
            <div className="lg:col-span-5 order-1 lg:order-2 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 self-start" style={{ backgroundColor: "hsla(215, 70%, 45%, 0.08)", border: "1px solid hsla(215, 70%, 45%, 0.15)" }}>
                <Radio className="w-4 h-4 animate-pulse" style={{ color: "hsl(215, 70%, 45%)" }} />
                <span className="text-xs font-bold tracking-wider uppercase" style={{ color: "hsl(215, 70%, 45%)" }}>Live Streams</span>
              </div>

              <h2 className="text-4xl lg:text-5xl leading-tight mb-6" style={{ color: "hsl(222, 47%, 18%)" }}>
                Study Stream
              </h2>

              <p className="text-base leading-relaxed mb-4" style={{ color: "hsl(222, 47%, 18%)" }}>
                Endless, free study streams you can join or host anytime. Participants keep cameras on, share a timer and occasionally check in.
              </p>
              <p className="text-sm leading-relaxed mb-8" style={{ color: "hsl(var(--p-muted))" }}>
                Ritualised check-ins and Pomodoro timers turn self-discipline into visible accountability while creating a sense of companionship.
              </p>

              <div className="flex gap-3">
                <a
                  href="https://app.csw.live"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-white transition-all hover:opacity-90"
                  style={{ backgroundColor: "hsl(215, 70%, 45%)" }}
                >
                  Join a Stream
                </a>
                <a
                  href="https://app.csw.live"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium transition-all hover:bg-gray-50"
                  style={{ border: "2px solid hsl(var(--p-border))", color: "hsl(222, 47%, 18%)" }}
                >
                  Host Your Own
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WAYS TO STUDY — Masonry-like ─── */}
      <section className="py-28 px-6 md:px-12" style={{ backgroundColor: "hsl(var(--p-cream))" }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-[0.2em] uppercase mb-3" style={{ color: "hsl(215, 70%, 45%)" }}>Community</p>
            <h2 className="text-4xl md:text-5xl mb-4" style={{ color: "hsl(222, 47%, 18%)" }}>
              Ways to Study Together
            </h2>
          </div>

          {/* 2 large + 4 small grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {studyModes.map((mode, index) => {
              const Icon = mode.icon;
              const isLarge = index < 2;
              return (
                <Link
                  key={mode.id}
                  to={mode.link}
                  className={`group relative overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                    isLarge ? "lg:col-span-2" : ""
                  }`}
                  style={{
                    backgroundColor: isLarge ? "hsl(222, 47%, 18%)" : "white",
                    border: isLarge ? "none" : "1px solid hsl(var(--p-border))",
                  }}
                >
                  <div className={`${isLarge ? "p-10" : "p-6"}`}>
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className={`${isLarge ? "w-14 h-14" : "w-10 h-10"} flex items-center justify-center`}
                        style={{ backgroundColor: isLarge ? "hsl(215, 70%, 45%)" : "hsl(222, 47%, 18%)" }}
                      >
                        <Icon className={`${isLarge ? "w-7 h-7" : "w-5 h-5"} text-white`} />
                      </div>
                      <h3
                        className={`${isLarge ? "text-2xl" : "text-lg"} font-bold`}
                        style={{ color: isLarge ? "white" : "hsl(222, 47%, 18%)", fontFamily: "'Playfair Display', serif" }}
                      >
                        {mode.title}
                      </h3>
                    </div>
                    <p
                      className={`${isLarge ? "text-sm" : "text-xs"} leading-relaxed`}
                      style={{ color: isLarge ? "hsla(0, 0%, 100%, 0.5)" : "hsl(var(--p-muted))" }}
                    >
                      {mode.description}
                    </p>
                    {isLarge && (
                      <div className="mt-6 flex items-center gap-2 text-sm font-bold" style={{ color: "hsl(210, 60%, 65%)" }}>
                        Learn more <ArrowRight className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── CTA — Split layout ─── */}
      <section className="relative overflow-hidden" style={{ backgroundColor: "hsl(215, 70%, 45%)" }}>
        <div className="grid lg:grid-cols-2">
          <div className="p-12 lg:p-20 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 self-start" style={{ backgroundColor: "hsla(0, 0%, 100%, 0.12)", border: "1px solid hsla(0, 0%, 100%, 0.2)" }}>
              <Sparkles className="w-4 h-4 text-white" />
              <span className="text-xs font-bold text-white uppercase tracking-wider">Join the Community</span>
            </div>

            <h2 className="text-3xl lg:text-5xl text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              Ready to Transform Your Productivity?
            </h2>
            <p className="leading-relaxed mb-8 text-white/60 max-w-lg">
              CSW transforms studying and working from a solitary chore into a supportive, engaging experience. Stop procrastinating, stay accountable and boost your output.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://app.csw.live"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-bold transition-all hover:scale-[1.02]"
                style={{ backgroundColor: "white", color: "hsl(222, 47%, 18%)" }}
              >
                Start Now – It's Free <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="https://app.csw.live"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-medium border transition-all hover:bg-white/10"
                style={{ borderColor: "hsla(0, 0%, 100%, 0.3)", color: "white" }}
              >
                Watch How It Works
              </a>
            </div>
          </div>

          {/* Right — stats panel */}
          <div className="p-12 lg:p-20 flex flex-col justify-center items-center" style={{ backgroundColor: "hsl(222, 47%, 18%)" }}>
            <div className="grid grid-cols-2 gap-8 w-full max-w-sm">
              {[
                { val: "10,000+", label: "Active Users" },
                { val: "24/7", label: "Study Rooms" },
                { val: "Free", label: "Forever" },
                { val: "Global", label: "Community" },
              ].map((s) => (
                <div key={s.label} className="text-center py-6" style={{ borderBottom: "2px solid hsla(215, 70%, 45%, 0.3)" }}>
                  <p className="text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{s.val}</p>
                  <p className="text-xs text-white/40 uppercase tracking-wider">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap justify-center gap-2">
              {['Students', 'Professionals', 'Creators', 'Researchers'].map((g) => (
                <span key={g} className="px-4 py-1.5 text-xs font-medium text-white/70" style={{ backgroundColor: "hsla(215, 70%, 45%, 0.15)", border: "1px solid hsla(215, 70%, 45%, 0.25)" }}>{g}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="py-14 px-6 md:px-12" style={{ backgroundColor: "hsl(222, 50%, 12%)" }}>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2 space-y-3">
            <span className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>CSW</span>
            <p className="text-sm text-white/50 max-w-xs">Chill, Study, Work — your personal productivity tool and online workspace for students and professionals.</p>
          </div>
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Features</h4>
            <ul className="space-y-2 text-sm text-white/50">
              <li><a href="/study-stream/" className="hover:text-white transition-colors">Study Stream</a></li>
              <li><a href="/study-timer/" className="hover:text-white transition-colors">Study Timer</a></li>
              <li><a href="/task-management/" className="hover:text-white transition-colors">Task Management</a></li>
              <li><a href="/flashcards/" className="hover:text-white transition-colors">Flashcards</a></li>
            </ul>
          </div>
          <div className="space-y-3">
            <h4 className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Community</h4>
            <ul className="space-y-2 text-sm text-white/50">
              <li><a href="/study-with-me/" className="hover:text-white transition-colors">Study With Me</a></li>
              <li><a href="/study-with-friends/" className="hover:text-white transition-colors">Study With Friends</a></li>
              <li><a href="/group-study/" className="hover:text-white transition-colors">Group Study</a></li>
              <li><a href="/blog/" className="hover:text-white transition-colors">Blog</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 pt-6 text-center text-xs text-white/30" style={{ borderTop: "1px solid hsla(0, 0%, 100%, 0.08)" }}>
          © {new Date().getFullYear()} CSW — Chill Study Work
        </div>
      </footer>
    </div>
  );
};

export default RedesignPreview;
